import {MobileTimePicker} from '@mui/x-date-pickers/MobileTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {useEffect,useState} from "react";
import {Box,TextField,InputAdornment,IconButton} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {getLocaleFromLang} from '../../../core/utilities/Utilities.js';
import dayjs from 'dayjs';
import {DefaultConfig,evaluateReadOnly} from '../../../core/utilities/Utilities';
import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import 'dayjs/locale/pt-br';
const readOnlyBackgroundColor=DefaultConfig.readOnlyBackgroundColor;

const FieldTime=({row,fieldName,application,recordRules,readOnly,setFieldValue,setFieldChanged})=>{
    const lang=application.globalData.lang;
    const locale=application.globalData.locale||lang;
    dayjs.locale(locale.toLowerCase());
    const fieldProp=row?.[fieldName];
    const fieldValue=fieldProp?.value;
    const readOnlyEvaluated=evaluateReadOnly(readOnly,recordRules,fieldProp);
    const localeText=getLocaleFromLang(application.globalData.lang);
    const [pickerOpen,setPickerOpen]=useState(false);
    const [inputValue,setInputValue]=useState(fieldValue||'');
    const [timeValue,setTimeValue]=useState(null);
    const [isFieldChanged,setIsFieldChanged]=useState(false);

    //Parses a raw HH:MM(:SS)-ish server value into this field's local display state - factored out
    //of the effect below so it's one place if the parsing ever needs to change.
    const syncFieldFromValue=(value)=>{
        if(!value){
            setInputValue('');
            setTimeValue(null);
            return;
        }
        let [hh,mm]=[0,0];
        if(value.includes(":")){
            [hh,mm]=value.split(':');
            if(mm.length===1){
                mm=parseInt(mm+'0');
            }
            if(hh.length===1){
                hh=parseInt(hh+'0');
            }
        }
        else{
            if(value.length===1){
                hh=parseInt(value+'0');
            }
            else{
                hh=parseInt(value);
            }
        }
        const newInputValue=hh+':'+mm;
        setInputValue(newInputValue);
        const newTimeValue=dayjs().hour(Number(hh)).minute(Number(mm)).second(0).millisecond(0);
        setTimeValue(newTimeValue);
    };

    useEffect(()=>{
        if(fieldValue){
            syncFieldFromValue(fieldValue);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[fieldValue]);

    const formatTimeInput=(raw)=>{
        const digits=raw.replace(/\D/g, '').slice(0,4);
        if(digits.length===0){return '';}
        if(digits.length<2){return digits;}
        if(digits.length===2){
            const previous_input_size=inputValue.length;
            if(previous_input_size===1){return `${digits.slice(0,2)}:`;}
        }
        const hours=digits.slice(0,2);
        const minutes=digits.slice(2);
        if(minutes){
            return `${hours}:${minutes}`;
        }
        else if(hours){
            const previous_input_size=inputValue.length;
            if(previous_input_size<=2){return `${hours}:`;}
            else{return hours;}
        }
        return '';
    };

    const handleInputChange=(e)=>{
        if(!isFieldChanged){setIsFieldChanged(true)}
        setFieldChanged(true);
        const formatted=formatTimeInput(e.target.value);
        let [hh,mm]=[0,0];
        if(formatted.includes(":")){
            [hh,mm]=formatted.split(':');
            if(mm.length===1){
                mm=parseInt(mm+'0');
            }
            if(hh.length===1){
                hh=parseInt(hh+'0');
            }
        }
        else{
            if(formatted.length===1){
                hh=parseInt(formatted+'0');
            }
            else{
                hh=parseInt(formatted);
            }
        }
        
        const newTimeValue = dayjs().hour(Number(hh)).minute(Number(mm)).second(0).millisecond(0);
        setTimeValue(newTimeValue);
        setInputValue(formatted);
    }
    const isValidTime=(value)=>{
        if(value.length===0){
            return true;
        }
        const match=/^(\d{1,2})(:(\d{1,2}))?$/.exec(value);
        if(!match){
            return false;
        }
        const hour=parseInt(match[1],10);
        const minute=match[3]?parseInt(match[3],10):0;
        if(hour<0||hour>24){return false;}
        if(minute<0||minute>59){return false};
        if(hour===24&&minute!==0){return false;}
        return true;
    };

    const handleBlur=()=>{
        if(isFieldChanged){
            setIsFieldChanged(false);
            if(inputValue.length===0||!isValidTime(inputValue)){
                setFieldValue(fieldName,null);
            }
            else{
                setFieldValue(fieldName,inputValue);
            }
        }
    }

    return (
        <Box sx={{flex:1,minWidth:0}}>
            <TextField
                InputProps={{
                    readOnly:readOnlyEvaluated,
                    maxLength:5,
                    inputMode:'numeric',
                    endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={()=>{!readOnlyEvaluated&&setPickerOpen(true);}}>
                            <AccessTimeIcon/>
                        </IconButton>
                    </InputAdornment>
                ),
                }}
                sx={{div:{backgroundColor:readOnlyEvaluated?readOnlyBackgroundColor:""}}}
                style={{marginTop:"0px",marginBottom:"8px"}}
                id={fieldName}
                name={fieldName}
                fullWidth
                required={fieldProp?.required?fieldProp?.required:false}
                error={fieldProp?.validation_error?fieldProp?.validation_error:false}
                helperText={fieldProp?.validation_error_message?fieldProp?.validation_error_message:''}
                variant="outlined"
                size="small"
                label={fieldProp?.ui_label_locale?application.getLocale(fieldProp?.ui_label_locale):fieldName}
                value={inputValue}
                margin="normal"
                onChange={handleInputChange}
                onBlur={handleBlur}
            />
       
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale.toLowerCase()} localeText={localeText}>
                <MobileTimePicker
                    open={pickerOpen}
                    closeOnSelect={false}
                    ampm={false}
                    onClose={()=>{
                        setPickerOpen(false);
                        if(fieldValue){
                            const [hh,mm]=fieldValue.split(':');
                            const original=dayjs().hour(Number(hh)).minute(Number(mm));
                            setTimeValue(original);
                        }
                        else{
                            setTimeValue(null);
                        }
                    }}
                    onChange={(newValue)=>{
                        if(newValue&&newValue.isValid()){
                            setTimeValue(newValue);
                        }
                    }}
                    value={timeValue}
                    onAccept={(newValue)=>{
                        setTimeValue(newValue);
                        setInputValue(newValue?.format('HH:mm'));
                        setFieldChanged(true);
                        setFieldValue(fieldName,newValue?.format('HH:mm'))
                    }}
                    slotProps={{
                        textField:{style:{display:'none'}},
                    }}
                />
            </LocalizationProvider>
        </Box>
    );
}

export default FieldTime;
